import { faCheck, faExclamationTriangle, faHourglassEnd, faHourglassHalf, faInfoCircle, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { corners, gap, paddingX, paddingY, Theme, typography } from "../../styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import styled from "styled-components";
import { applyOpacity } from "../../styles/utils";
type NotificationProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string;
  variant: NotificationVariant;
};
export type NotificationVariant = "success" | "info" | "recurrenceInfo" | "timeInfo" | "warning" | "timeWarning";

/**
 * Resolves the colour for a given notification variant from the current
 * theme's (already theme-reactive, see `theme.ts`'s `computeTheme`)
 * `colours.success`/`.error`/`.info`, rather than the static
 * `nonCustomisableColours` module-level constant — so notification
 * colours correctly pick up the dark-theme THEME_TOKENS values
 * (success `#4ade80`, error `#ff5064`, info `#3c99e9`) instead of always
 * rendering the light-theme colours regardless of the active brand theme.
 */
export function getNotificationColour(theme: Theme, variant: NotificationVariant): string {
  switch (variant) {
    case "success":
      return theme.colours.success;
    case "info":
    case "recurrenceInfo":
    case "timeInfo":
      return theme.colours.info;
    case "warning":
    case "timeWarning":
      return theme.colours.error;
  }
}

/* eslint-disable sort-keys */
const iconMap: Record<NotificationVariant, React.ReactNode> = {
  success: <FontAwesomeIcon icon={faCheck} />,
  info: <FontAwesomeIcon icon={faInfoCircle} />,
  recurrenceInfo: <FontAwesomeIcon icon={faSyncAlt} />,
  timeInfo: <FontAwesomeIcon icon={faHourglassHalf} />,
  warning: <FontAwesomeIcon icon={faExclamationTriangle} />,
  timeWarning: <FontAwesomeIcon icon={faHourglassEnd} />
};
/* eslint-enable sort-keys */

const StyledNotification = styled.div<NotificationProps>`
  min-height: 2.5rem;
  width: 100%;
  ${corners.m}

  display: flex;
  align-items: center;

  background: ${({
  theme,
  variant
}) => applyOpacity(getNotificationColour(theme, variant), 0.05, false)};
  color: ${({
  theme,
  variant
}) => getNotificationColour(theme, variant)};
  ${paddingY.s}
  ${paddingX.m}
  ${typography.body}
  ${gap.s}
`;
export function Notification({
  children,
  ...props
}: NotificationProps) {
  return <StyledNotification {...props} data-sentry-element="StyledNotification" data-sentry-component="Notification" data-sentry-source-file="Notification.tsx">
      {iconMap[props.variant]}
      {children}
    </StyledNotification>;
}
export default Notification;